import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL, niceName } from "@/lib/format";
import { PoweredBy } from "@/components/PoweredBy";
import { useBranding, applyBrandVars } from "@/lib/branding";
import { Plus, LogOut, Home, UserRound, Sun, FolderOpen } from "lucide-react";
import { NotificationsBell } from "@/components/NotificationsBell";
import { ProfileDialog } from "@/components/Perfil";
import { ViewAs } from "@/components/ViewAs";
import { GlobalSearch } from "@/components/GlobalSearch";
import { useState, useEffect } from "react";
import { SetupAutoOpen } from "@/components/setup/SetupWizard";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Layout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const brand = useBranding();
  const dark = !!brand?.header_dark;
  const hc = brand?.header_color || "#2B3A4A";
  const [profile, setProfile] = useState(false);
  useEffect(() => { applyBrandVars(dark ? hc : brand?.color); }, [brand?.color, dark, hc]);

  const isCons = user.role === "consultor";
  const accent = brand?.accent_color || brand?.color || "#12332B";
  const newColor = brand?.logo_color || accent;
  const nav = [
    { to: "/visao", label: "Visão da agência", roles: ["direccao"] },
    { to: "/equipa", label: "Equipa", roles: ["direccao"] },
    { to: "/painel", label: "Painel", roles: ["gestor"] },
    { to: "/dia", label: "O meu dia", roles: ["consultor"] },
    { to: "/processos", label: "Processos", roles: ["gestor", "consultor", "direccao"] },
    { to: "/escrituras", label: "Escrituras", roles: ["gestor"] },
    { to: "/obrigacoes", label: "Obrigações legais", roles: user.impic_access ? ["direccao", "gestor"] : [] },
    { to: "/arquivo-kyc", label: "Arquivo KYC", roles: ["direccao"] },
    { to: "/definicoes", label: "Definições", roles: ["direccao"] },
  ].filter((n) => n.roles.includes(user.role));
  const canCreate = user.role === "consultor";

  const isActive = (to) => loc.pathname.startsWith(to);

  return (
    <div className="App min-h-screen flex flex-col font-body bg-ec-bg" style={{ "--hc": hc, "--ac": accent }}>
      <SetupAutoOpen user={user} />
      <header className={`sticky top-0 z-50 ${dark ? "text-white" : "bg-white text-ec-green border-b-2"}`}
        style={dark ? { backgroundColor: hc } : { borderBottomColor: brand?.color || "#e2e8f0" }} data-testid="app-header">
        <div className={`max-w-[1440px] mx-auto w-full ${dark ? "pr-5 md:pr-10" : "px-5 md:px-10 py-3"} flex items-center justify-between gap-4`}>
          <Link to="/inicio" className="flex items-center gap-2.5 min-w-0" data-testid="brand-home">
            {brand?.logo
              ? <img src={brand.logo} alt={brand.name} className={dark ? "h-16 w-16 shrink-0 object-cover" : "h-8 max-w-[110px] shrink-0 object-contain"} data-testid="brand-logo" />
              : <Home size={20} strokeWidth={1.75} className={dark ? "text-white ml-5" : "text-ec-green"} />}
            <span className={`font-heading text-xl tracking-wide truncate ${dark ? "text-white ml-3" : "text-ec-green"}`} data-testid="brand-portal-name">
              {brand?.name || ""}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 flex-1 ml-6">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={`px-3.5 py-2 rounded-lg text-sm transition-colors ${
                  dark
                    ? isActive(n.to) ? "bg-white/15 text-white font-medium" : "text-white/75 hover:text-white hover:bg-white/10"
                    : isActive(n.to) ? "bg-ec-green/10 text-ec-green font-medium" : "text-slate-600 hover:text-ec-green hover:bg-slate-50"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-2.5 shrink-0">
            {["gestor", "consultor"].includes(user.role) && <GlobalSearch dark={dark} />}
            <NotificationsBell />
            {canCreate && <button
              onClick={() => navigate("/novo")}
              data-testid="new-process-btn"
              className="hidden sm:flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-[filter] hover:brightness-110 shadow-sm"
              style={{ backgroundColor: newColor }}
            >
              <Plus size={16} strokeWidth={2} /> Nova angariação
            </button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  className="w-9 h-9 rounded-full overflow-hidden bg-slate-100 border border-slate-200 text-ec-green font-semibold grid place-items-center hover:bg-slate-200 transition-colors"
                >
                  {user.photo ? <img src={user.photo} alt="" className="w-full h-full object-cover" data-testid="user-menu-photo" /> : (user.name || "?").slice(0, 1).toUpperCase()}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{niceName(user.name)}</div>
                  <div className="text-xs text-slate-500 font-normal">{ROLE_LABEL[user.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfile(true)} data-testid="profile-menu-item">
                  <UserRound size={15} className="mr-2" /> O meu perfil
                </DropdownMenuItem>
                {user.agency_is_demo && <ViewAs />}
                {canCreate && <DropdownMenuItem onClick={() => navigate("/novo")} className="sm:hidden" data-testid="new-process-mobile">
                  <Plus size={15} className="mr-2" /> Nova angariação
                </DropdownMenuItem>}
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut size={15} className="mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <ProfileDialog open={profile} onOpenChange={setProfile} />
          </div>
        </div>

        {/* mobile nav */}
        <nav className={`md:hidden ${isCons ? "hidden" : "flex"} flex-wrap items-center gap-1 px-3 pb-2.5 ${dark ? "pt-1" : "-mt-1"}`} data-testid="mobile-nav">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`px-2.5 py-1.5 rounded-lg text-[13px] whitespace-nowrap transition-colors ${
                dark ? (isActive(n.to) ? "bg-white/15 text-white" : "text-white/75") : isActive(n.to) ? "bg-ec-green/10 text-ec-green" : "text-slate-600"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
      </header>

      <main className={`flex-grow max-w-[1440px] mx-auto w-full px-5 md:px-10 py-8 md:py-10 ${isCons ? "pb-10 md:pb-10" : ""}`}>
        <Outlet />
      </main>
      {isCons && <BottomBar isActive={isActive} accent={newColor} onProfile={() => setProfile(true)} />}

      <footer className={`py-5 px-6 text-center border-t border-slate-200 bg-white/60 backdrop-blur-sm ${isCons ? "pb-24 md:pb-5" : ""}`}>
        <p className="text-xs text-slate-500">
          A ferramenta organiza e lembra; não dá aconselhamento jurídico.
        </p>
        {brand && !brand.white_label && <p className="mt-1"><PoweredBy testid="app-powered-by" /></p>}
      </footer>
    </div>
  );
}

function BottomBar({ isActive, accent, onProfile }) {
  const item = (to, label, Icon) => (
    <Link to={to} className={`flex flex-col items-center gap-0.5 text-[11px] ${isActive(to) ? "navy-text font-semibold" : "text-slate-500"}`} data-testid={`bottom-${label.toLowerCase()}`}>
      <Icon size={20} />{label}
    </Link>
  );
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-slate-200 grid grid-cols-4 items-end px-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-testid="bottom-bar">
      {item("/dia", "O meu dia", Sun)}
      {item("/processos", "Processos", FolderOpen)}
      <Link to="/novo" className="justify-self-center -mt-6 w-14 h-14 rounded-full grid place-items-center text-white shadow-lg" style={{ backgroundColor: accent }} aria-label="Nova angariação" data-testid="bottom-new">
        <Plus size={26} />
      </Link>
      <button onClick={onProfile} className="flex flex-col items-center gap-0.5 text-[11px] text-slate-500" data-testid="bottom-perfil"><UserRound size={20} />Perfil</button>
    </nav>
  );
}
